import { useEffect, useRef, useState } from 'react'

export interface LiveRefreshState {
  /** 空字符串表示不显示；updated=刚自动刷新，pending=有新数据但用户正在操作。 */
  notice: '' | 'updated' | 'pending'
  updatedAt: string
  applyNow: () => void
}

interface Options {
  fetchRevision: () => Promise<string>
  apply: () => void
  intervalMs?: number
  idleMs?: number
  enabled?: boolean
}

const isEditing = () => {
  const active = document.activeElement as HTMLElement | null
  if (active && ['INPUT', 'TEXTAREA', 'SELECT'].includes(active.tagName)) return true
  return Boolean(document.querySelector('[role="dialog"], .modal, .modal-backdrop'))
}

/**
 * 官网实时刷新：定时对比公开内容（或登录用户资产）版本，
 * 有变化时自动重新拉取数据；用户正在填写表单时只提示，点击后刷新。
 */
export const useLiveRefresh = ({
  fetchRevision, apply, intervalMs = 20_000, idleMs = 60_000, enabled = true
}: Options): LiveRefreshState => {
  const [notice, setNotice] = useState<'' | 'updated' | 'pending'>('')
  const [updatedAt, setUpdatedAt] = useState('')
  const appliedRevision = useRef<string | null>(null)
  const pendingRevision = useRef<string | null>(null)
  const lastInteraction = useRef(0)
  const fetchRef = useRef(fetchRevision)
  const applyRef = useRef(apply)
  fetchRef.current = fetchRevision
  applyRef.current = apply

  const consume = (revision: string) => {
    appliedRevision.current = revision
    pendingRevision.current = null
    applyRef.current()
    setUpdatedAt(new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }))
    setNotice('updated')
  }

  useEffect(() => {
    if (!enabled) return
    const remember = () => { lastInteraction.current = Date.now() }
    window.addEventListener('pointerdown', remember, true)
    window.addEventListener('keydown', remember, true)

    let cancelled = false
    const poll = async () => {
      try {
        const revision = await fetchRef.current()
        if (cancelled) return
        if (appliedRevision.current === null) {
          appliedRevision.current = revision
          return
        }
        if (revision === appliedRevision.current) {
          if (pendingRevision.current && !isEditing() && Date.now() - lastInteraction.current > 5_000) {
            consume(pendingRevision.current)
          }
          return
        }
        if (isEditing() || Date.now() - lastInteraction.current < idleMs) {
          pendingRevision.current = revision
          setNotice('pending')
          return
        }
        consume(revision)
      } catch {
        // 静默跳过
      }
    }

    void poll()
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void poll()
    }, intervalMs)
    const onVisible = () => { if (document.visibilityState === 'visible') void poll() }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      cancelled = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('pointerdown', remember, true)
      window.removeEventListener('keydown', remember, true)
    }
  }, [intervalMs, idleMs, enabled])

  useEffect(() => {
    if (notice !== 'updated') return
    const timer = window.setTimeout(() => setNotice(''), 4_000)
    return () => window.clearTimeout(timer)
  }, [notice])

  return {
    notice,
    updatedAt,
    applyNow: () => { if (pendingRevision.current) consume(pendingRevision.current) }
  }
}
